
import { useState, useEffect, useMemo } from 'react';
import { useRentalNotificationsFixed } from './useRentalNotificationsFixed';
import { useSubscriptionNotifications } from './useSubscriptionNotifications';
import { usePaymentNotifications } from './usePaymentNotifications';
import { Notification, NotificationData } from './types';
import { 
  addNotification as addNotificationService, 
  getNotifications, 
  markNotificationAsRead, 
  showAllNotifications 
} from '@/services/notificationService';

export const useNotificationsManager = () => {
  // Состояние для менеджера уведомлений
  const [managerNotifications, setManagerNotifications] = useState<Notification[]>([]);
  
  // Получаем уведомления из разных источников
  const { notifications: rentalNotifications } = useRentalNotificationsFixed();
  const subscriptionNotifications = useSubscriptionNotifications();
  const paymentNotifications = usePaymentNotifications();
  
  // Синхронизируем локальное состояние с глобальным хранилищем уведомлений при монтировании
  useEffect(() => {
    setManagerNotifications(getNotifications());
    
    // Чтобы избежать множественных вызовов, устанавливаем интервал для обновления
    const refreshInterval = setInterval(() => {
      setManagerNotifications(getNotifications());
    }, 60000); // Обновляем раз в минуту
    
    return () => clearInterval(refreshInterval);
  }, []);
  
  // Объединяем все уведомления в один массив
  const allNotifications = useMemo(() => {
    // Получаем актуальные уведомления из централизованного хранилища
    const centralNotifications = getNotifications();
    
    // Создаем набор ID для быстрой проверки дубликатов
    const notificationIds = new Set(centralNotifications.map(n => n.id));
    
    // Добавляем уведомления из других источников, если их нет в централизованном хранилище
    const uniqueRentalNotifications = (rentalNotifications || [])
      .filter(n => !notificationIds.has(n.id));
    
    const uniqueSubscriptionNotifications = (subscriptionNotifications || [])
      .filter(n => !notificationIds.has(n.id));
    
    const uniquePaymentNotifications = (paymentNotifications || [])
      .filter(n => !notificationIds.has(n.id));
    
    // Объединяем все уникальные уведомления
    return [
      ...centralNotifications,
      ...uniqueRentalNotifications,
      ...uniqueSubscriptionNotifications,
      ...uniquePaymentNotifications
    ];
  }, [managerNotifications, rentalNotifications, subscriptionNotifications, paymentNotifications]);
  
  // Подсчитываем общее количество уведомлений
  const totalCount = useMemo(() => allNotifications.length, [allNotifications]);
  
  // Подсчитываем количество непрочитанных уведомлений
  const unreadCount = useMemo(() => 
    allNotifications.filter(notification => !notification.read).length,
    [allNotifications]
  );
  
  // Функция для показа всех уведомлений
  const showNotificationsHandler = () => {
    showAllNotifications();
    setManagerNotifications(getNotifications());
  };
  
  // Функция для отметки уведомления как прочитанного
  const markAsRead = (id: string) => {
    const updatedNotifications = markNotificationAsRead(id);
    setManagerNotifications(updatedNotifications);
  };
  
  // Функция для добавления нового уведомления
  const addNotification = (data: NotificationData) => {
    const newNotification = addNotificationService(data);
    if (newNotification) {
      setManagerNotifications(getNotifications());
    }
    return newNotification;
  };
  
  return {
    totalCount,
    notifications: allNotifications,
    unreadCount,
    showAllNotifications: showNotificationsHandler,
    markAsRead,
    addNotification,
    rentalNotifications,
    subscriptionNotifications,
    paymentNotifications,
  };
};
